import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Lightbulb,
  Plus,
  Trash2,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { CollapsibleMarkdownEditor } from '@/components/admin/shared/collapsible-markdown-editor'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { ForceExpandState } from '@/hooks/use-force-expand-state'
import { useForceExpandState } from '@/hooks/use-force-expand-state'
import { getDifficultyLabel } from '@/lib/difficulty'
import { cn } from '@/lib/utils'
import { Badge } from '../../shared/badge'
import { DIFFICULTY_LEVELS, SOLUTION_TYPES } from '../constants'
import type {
  ApproachComplexityEntry,
  ApproachContentBlock,
  ApproachSolution,
  ApproachStrategy,
  DifficultyInfo,
  QuestionApproach,
} from '../types'
import { ApproachCodeEditor } from './approach-code-editor'
import { ApproachComplexityEditor } from './approach-complexity-editor'
import { RecipeBlockEditor, type RecipeSummary } from './recipe-block-editor'
import { StrategySearchInput } from './strategy-search-input'

// =============================================================================
// Types
// =============================================================================

interface ApproachHint {
  id?: string
  content: string
  order: number
}

interface ApproachWithHints extends QuestionApproach {
  hints?: ApproachHint[]
}

interface ApproachBlockEditorProps {
  approach: ApproachWithHints
  index: number
  isExpanded: boolean
  onToggleExpanded: () => void
  onUpdate: (updates: Partial<ApproachWithHints>) => void
  onDelete: () => void
  allStrategies: ApproachStrategy[]
  /** All available recipes for recipe block selection */
  allRecipes?: RecipeSummary[]
  problemSlug?: string
  functionNameOverride?: string
  dragHandleProps?: Record<string, unknown>
  forceExpandState?: ForceExpandState | null
}

// =============================================================================
// Sortable Block Wrapper
// =============================================================================

function SortableContentBlock({
  id,
  children,
}: {
  id: string
  children: (props: {
    dragHandleProps: Record<string, unknown>
    isDragging: boolean
  }) => React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <div ref={setNodeRef} style={style}>
      {children({
        dragHandleProps: { ...attributes, ...listeners },
        isDragging,
      })}
    </div>
  )
}

// =============================================================================
// Content Block Components
// =============================================================================

function CodeBlockEditor({
  dragHandleProps,
  solutions,
  onUpdateSolutions,
  approachComplexities,
  onUpdateComplexities,
  defaultFunctionName,
  problemSlug,
  functionNameOverride,
  defaultExpanded = true,
  forceExpandState,
  isDragging = false,
}: {
  dragHandleProps: Record<string, unknown>
  solutions: ApproachSolution[]
  onUpdateSolutions: (solutions: ApproachSolution[]) => void
  approachComplexities: ApproachComplexityEntry[]
  onUpdateComplexities: (complexities: ApproachComplexityEntry[]) => void
  defaultFunctionName?: string
  problemSlug?: string
  functionNameOverride?: string
  defaultExpanded?: boolean
  forceExpandState?: ForceExpandState | null
  /** When true, hides Monaco editor to prevent disposal issues during drag */
  isDragging?: boolean
}) {
  const [isExpanded, setIsExpanded] = useForceExpandState(
    forceExpandState,
    defaultExpanded,
  )

  // Get languages that have solutions
  const getLanguagePreview = () => {
    const languagesWithCode = solutions
      .filter((s) => s.code && s.code.trim().length > 0)
      .map((s) => s.language)
    if (languagesWithCode.length === 0) return 'No solutions yet'
    return languagesWithCode.join(', ')
  }

  return (
    <div className="border-border rounded-lg border">
      <div
        className="border-border/50 flex cursor-pointer items-center gap-2 border-b px-3 py-2"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <button
          type="button"
          className="cursor-grab touch-none active:cursor-grabbing"
          {...dragHandleProps}
          aria-label="Drag to reorder"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="text-muted-foreground h-4 w-4" />
        </button>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground p-0.5 transition-colors"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
          Code
        </span>
        {!isExpanded && (
          <span className="text-muted-foreground/70 ml-2 truncate text-xs italic">
            {getLanguagePreview()}
          </span>
        )}
      </div>
      {isExpanded && (
        <>
          <div className="p-3">
            {isDragging ? (
              // Show placeholder during drag to prevent Monaco disposal issues
              <div className="bg-muted/50 text-muted-foreground flex h-[150px] items-center justify-center rounded-lg border border-dashed text-sm">
                Drop to reorder...
              </div>
            ) : (
              <ApproachCodeEditor
                solutions={solutions}
                onUpdate={onUpdateSolutions}
                problemSlug={problemSlug}
                functionNameOverride={functionNameOverride}
                forceExpandState={forceExpandState}
              />
            )}
          </div>
          <ApproachComplexityEditor
            complexities={approachComplexities}
            onUpdate={onUpdateComplexities}
            defaultFunctionName={defaultFunctionName}
            forceExpandState={forceExpandState}
          />
        </>
      )}
    </div>
  )
}

// =============================================================================
// Hints Section (low-profile collapsible)
// =============================================================================

function HintsSection({
  hints,
  onUpdate,
  forceExpandState,
}: {
  hints: ApproachHint[]
  onUpdate: (hints: ApproachHint[]) => void
  forceExpandState?: ForceExpandState | null
}) {
  const [isExpanded, setIsExpanded] = useForceExpandState(
    forceExpandState,
    false,
  )

  const handleAddHint = () => {
    onUpdate([...hints, { content: '', order: hints.length }])
  }

  const handleUpdateHint = (index: number, content: string) => {
    const updated = [...hints]
    updated[index] = { ...updated[index], content }
    onUpdate(updated)
  }

  const handleRemoveHint = (index: number) => {
    onUpdate(
      hints.filter((_, i) => i !== index).map((h, i) => ({ ...h, order: i })),
    )
  }

  return (
    <div className="border-border rounded-lg border">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="hover:bg-muted/50 flex w-full items-center gap-2 px-3 py-2 text-left transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        ) : (
          <ChevronRight className="text-muted-foreground h-4 w-4" />
        )}
        <Lightbulb className="text-muted-foreground h-4 w-4" />
        <span className="text-sm font-medium">Hints</span>
        {hints.length > 0 && (
          <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs">
            {hints.length}
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="border-border space-y-2 border-t p-3">
          {hints.length === 0 ? (
            <p className="text-muted-foreground text-center text-sm">
              No hints for this approach.
            </p>
          ) : (
            hints.map((hint, i) => (
              <div key={hint.id || `hint-${i}`} className="flex gap-2">
                <span className="text-muted-foreground mt-2 text-xs">
                  {i + 1}.
                </span>
                <textarea
                  value={hint.content}
                  onChange={(e) => handleUpdateHint(i, e.target.value)}
                  placeholder="Hint for this approach..."
                  className="border-input bg-background min-h-[60px] flex-1 resize-y rounded-md border px-3 py-2 text-sm"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveHint(i)}
                  className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddHint}
            className="w-full"
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Hint
          </Button>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function ApproachBlockEditor({
  approach,
  index,
  isExpanded,
  onToggleExpanded,
  onUpdate,
  onDelete,
  allStrategies,
  allRecipes = [],
  problemSlug,
  functionNameOverride,
  dragHandleProps,
  forceExpandState,
}: ApproachBlockEditorProps) {
  const solutionTypeConfig =
    SOLUTION_TYPES.find((t) => t.value === approach.solutionType) ||
    SOLUTION_TYPES[1]

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Track when any block is being dragged - used to hide Monaco editors
  // to prevent "InstantiationService has been disposed" errors
  const [isAnyBlockDragging, setIsAnyBlockDragging] = useState(false)

  // Ensure blocks always include exactly one code block
  // Default blocks if none exist, or ensure code block exists
  // IMPORTANT: This must be memoized to prevent generating new UUIDs on every render,
  // which would cause child components to remount and lose their state (e.g., expanded state)
  const blocks = useMemo((): ApproachContentBlock[] => {
    if (!approach.blocks || approach.blocks.length === 0) {
      return [
        { type: 'markdown', id: crypto.randomUUID(), content: '' },
        { type: 'code', id: crypto.randomUUID() },
      ]
    }
    // If existing blocks don't have a code block, add one at the end
    const hasCodeBlock = approach.blocks.some((b) => b.type === 'code')
    if (!hasCodeBlock) {
      return [...approach.blocks, { type: 'code', id: crypto.randomUUID() }]
    }
    return approach.blocks
  }, [approach.blocks])

  const handleDragStart = useCallback(() => {
    setIsAnyBlockDragging(true)
  }, [])

  const handleDragCancel = useCallback(() => {
    setIsAnyBlockDragging(false)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsAnyBlockDragging(false)

      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = blocks.findIndex((b) => b.id === active.id)
      const newIndex = blocks.findIndex((b) => b.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      onUpdate({ blocks: arrayMove(blocks, oldIndex, newIndex) })
    },
    [blocks, onUpdate],
  )

  const handleUpdateBlock = useCallback(
    (blockId: string, updates: Partial<ApproachContentBlock>) => {
      onUpdate({
        blocks: blocks.map((b) =>
          b.id === blockId ? { ...b, ...updates } : b,
        ) as ApproachContentBlock[],
      })
    },
    [blocks, onUpdate],
  )

  const handleDeleteBlock = useCallback(
    (blockId: string) => {
      // Only allow deletion of markdown and recipe blocks, not code blocks
      const blockToDelete = blocks.find((b) => b.id === blockId)
      if (blockToDelete?.type === 'code') return
      onUpdate({ blocks: blocks.filter((b) => b.id !== blockId) })
    },
    [blocks, onUpdate],
  )

  const handleAddMarkdownBlock = useCallback(() => {
    const newBlock: ApproachContentBlock = {
      type: 'markdown',
      id: crypto.randomUUID(),
      content: '',
    }
    onUpdate({ blocks: [...blocks, newBlock] })
  }, [blocks, onUpdate])

  const handleAddRecipeBlock = useCallback(() => {
    const newBlock: ApproachContentBlock = {
      type: 'recipe',
      id: crypto.randomUUID(),
      recipeId: '',
    }
    onUpdate({ blocks: [...blocks, newBlock] })
  }, [blocks, onUpdate])

  return (
    <div className="border-border overflow-hidden rounded-lg border">
      {/* Collapsed Header */}
      <button
        type="button"
        onClick={onToggleExpanded}
        className="hover:bg-muted/50 flex w-full items-center gap-2 p-3 text-left transition-colors"
      >
        {dragHandleProps && (
          <div
            className="cursor-grab touch-none active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            {...dragHandleProps}
          >
            <GripVertical className="text-muted-foreground h-4 w-4" />
          </div>
        )}

        <span className="text-muted-foreground font-mono text-sm">
          #{index + 1}
        </span>

        <Badge variant={approach.solutionType ?? undefined}>
          {solutionTypeConfig.label}
        </Badge>

        {approach.difficulty && (
          <Badge variant={approach.difficulty.name}>
            {getDifficultyLabel(approach.difficulty)}
          </Badge>
        )}

        {approach.strategies && approach.strategies.length > 0 && (
          <div className="flex gap-1">
            {approach.strategies.slice(0, 3).map((strategy) => (
              <Badge key={strategy.id} variant="topic" className="text-[10px]">
                {strategy.name}
              </Badge>
            ))}
            {approach.strategies.length > 3 && (
              <span className="text-muted-foreground text-xs">
                +{approach.strategies.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex-1" />

        {approach.name && (
          <span className="text-muted-foreground max-w-[200px] truncate text-sm">
            {approach.name}
          </span>
        )}

        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-border space-y-4 border-t p-4">
          {/* Metadata Row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Name */}
            <div className="min-w-[200px] flex-1">
              <Input
                value={approach.name || ''}
                onChange={(e) => onUpdate({ name: e.target.value })}
                placeholder="Approach name..."
                className="h-8"
              />
            </div>

            {/* Solution Type */}
            <div className="flex gap-1">
              {SOLUTION_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => onUpdate({ solutionType: type.value })}
                  className={cn(
                    'rounded-md border p-0 transition-colors',
                    approach.solutionType === type.value
                      ? 'border-primary'
                      : 'border-transparent hover:border-border',
                  )}
                >
                  <Badge variant={type.value}>{type.label}</Badge>
                </button>
              ))}
            </div>

            {/* Difficulty */}
            <div className="flex gap-1">
              {DIFFICULTY_LEVELS.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() =>
                    onUpdate({
                      difficulty: { name: level.value } as DifficultyInfo,
                    })
                  }
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs transition-colors',
                    approach.difficulty?.name === level.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted',
                  )}
                >
                  {level.label}
                </button>
              ))}
            </div>

            {/* Strategies */}
            <StrategySearchInput
              selectedStrategies={approach.strategies || []}
              allStrategies={allStrategies}
              onChange={(strategies) => onUpdate({ strategies })}
            />

            {/* Delete button */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-destructive hover:bg-destructive/10"
            >
              <Trash2 size={14} />
            </Button>
          </div>

          {/* Content Blocks */}
          <div className="space-y-3">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={blocks.map((b) => b.id)}
                strategy={verticalListSortingStrategy}
              >
                {blocks.map((block) => (
                  <SortableContentBlock key={block.id} id={block.id}>
                    {({ dragHandleProps: blockDragProps, isDragging }) => {
                      if (block.type === 'markdown') {
                        return (
                          <CollapsibleMarkdownEditor
                            content={block.content}
                            onChange={(content) =>
                              handleUpdateBlock(block.id, { content })
                            }
                            onDelete={() => handleDeleteBlock(block.id)}
                            dragHandleProps={blockDragProps}
                            forceExpandState={forceExpandState}
                          />
                        )
                      }
                      if (block.type === 'code') {
                        return (
                          <CodeBlockEditor
                            dragHandleProps={blockDragProps}
                            solutions={approach.solutions || []}
                            onUpdateSolutions={(solutions) =>
                              onUpdate({ solutions })
                            }
                            approachComplexities={approach.complexities || []}
                            onUpdateComplexities={(complexities) =>
                              onUpdate({ complexities })
                            }
                            defaultFunctionName={functionNameOverride}
                            problemSlug={problemSlug}
                            functionNameOverride={functionNameOverride}
                            forceExpandState={forceExpandState}
                            isDragging={isAnyBlockDragging}
                          />
                        )
                      }
                      if (block.type === 'recipe') {
                        return (
                          <RecipeBlockEditor
                            recipeId={block.recipeId || null}
                            allRecipes={allRecipes}
                            onChange={(recipeId) =>
                              handleUpdateBlock(block.id, {
                                recipeId: recipeId || '',
                              })
                            }
                            onDelete={() => handleDeleteBlock(block.id)}
                            dragHandleProps={blockDragProps}
                            forceExpandState={forceExpandState}
                          />
                        )
                      }
                      // Complexity blocks are deprecated - complexity is now per-solution in the code block
                      return null
                    }}
                  </SortableContentBlock>
                ))}
              </SortableContext>
            </DndContext>

            {/* Add Block Buttons */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddMarkdownBlock}
              >
                <Plus className="mr-1 h-3 w-3" />
                Markdown
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddRecipeBlock}
                className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
              >
                <Plus className="mr-1 h-3 w-3" />
                Recipe
              </Button>
            </div>
          </div>

          {/* Hints Section */}
          <HintsSection
            hints={approach.hints || []}
            onUpdate={(hints) => onUpdate({ hints })}
            forceExpandState={forceExpandState}
          />
        </div>
      )}
    </div>
  )
}
